import React from 'react';
import { colors, spacing, typography, borderRadius, transitions } from '../../theme/tokens';

/**
 * Tabs Component
 *
 * A flexible tabbed navigation component with multiple style variants.
 *
 * @param {Array} tabs - Array of tab objects: [{ id: 'tab1', label: 'Tab 1', icon: 'fas fa-icon' }]
 * @param {string} activeTab - ID of currently active tab
 * @param {function} onChange - Callback when tab is clicked: (tabId) => void
 * @param {string} variant - 'default' | 'pills' | 'underline' (default: 'default')
 * @param {string} size - 'sm' | 'md' | 'lg' (default: 'md')
 * @param {boolean} fullWidth - Whether tabs should fill full width (default: false)
 */
const Tabs = ({
  tabs = [],
  activeTab,
  onChange,
  variant = 'default',
  size = 'md',
  fullWidth = false,
  className = '',
}) => {
  const sizes = {
    sm: { padding: `${spacing.sm} ${spacing.md}`, fontSize: typography.fontSize.xs },
    md: { padding: `${spacing.md} ${spacing.xl}`, fontSize: typography.fontSize.sm },
    lg: { padding: `${spacing.lg} ${spacing.xxl}`, fontSize: typography.fontSize.md },
  };

  const sizeStyle = sizes[size] || sizes.md;

  // Default variant (like current implementation)
  const getDefaultTabStyle = (isActive) => ({
    padding: sizeStyle.padding,
    fontSize: sizeStyle.fontSize,
    fontWeight: typography.fontWeight.medium,
    background: 'transparent',
    color: isActive ? colors.accent : colors.text.secondary,
    border: 'none',
    borderBottom: isActive ? `2px solid ${colors.accent}` : `2px solid ${colors.border.default}`,
    cursor: 'pointer',
    transition: transitions.normal,
    flex: fullWidth ? 1 : 'none',
    whiteSpace: 'nowrap',
  });

  // Pills variant (rounded tabs with background)
  const getPillsTabStyle = (isActive) => ({
    padding: sizeStyle.padding,
    fontSize: sizeStyle.fontSize,
    fontWeight: typography.fontWeight.medium,
    background: isActive ? colors.accent : 'transparent',
    color: isActive ? colors.text.primary : colors.text.secondary,
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    transition: transitions.normal,
    flex: fullWidth ? 1 : 'none',
    whiteSpace: 'nowrap',
    margin: `0 ${spacing.xs}`,
  });

  // Underline variant (minimal with bottom border only)
  const getUnderlineTabStyle = (isActive) => ({
    padding: sizeStyle.padding,
    fontSize: sizeStyle.fontSize,
    fontWeight: typography.fontWeight.medium,
    background: 'transparent',
    color: isActive ? colors.text.primary : colors.text.secondary,
    border: 'none',
    borderBottom: isActive ? `2px solid ${colors.text.primary}` : '2px solid transparent',
    cursor: 'pointer',
    transition: transitions.normal,
    flex: fullWidth ? 1 : 'none',
    whiteSpace: 'nowrap',
  });

  const getTabStyle = (isActive) => {
    switch (variant) {
      case 'pills':
        return getPillsTabStyle(isActive);
      case 'underline':
        return getUnderlineTabStyle(isActive);
      default:
        return getDefaultTabStyle(isActive);
    }
  };

  const containerStyle = {
    display: 'flex',
    gap: variant === 'pills' ? spacing.xs : '0',
    borderBottom: variant === 'pills' ? 'none' : `1px solid ${colors.border.default}`,
    marginBottom: spacing.lg,
    overflowX: 'auto',
    overflowY: 'hidden',
  };

  return (
    <div style={containerStyle} className={className}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={getTabStyle(isActive)}
            onMouseEnter={(e) => {
              if (!isActive && variant !== 'pills') {
                e.target.style.color = colors.text.primary;
                if (variant === 'default') {
                  e.target.style.borderBottomColor = colors.border.focus;
                }
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.target.style.color = colors.text.secondary;
                if (variant === 'default') {
                  e.target.style.borderBottomColor = colors.border.default;
                }
              }
            }}
          >
            {tab.icon && (
              <i
                className={tab.icon}
                style={{ marginRight: spacing.sm, color: colors.text.primary }}
              />
            )}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
